import type { ListOccasion } from "@listou/shared/constants";
import type { AdminListSort, AdminProductsQuery } from "@listou/shared/schemas/admin";
import type { CatalogueQuery } from "@listou/shared/schemas/lists";
import { infiniteQueryOptions, keepPreviousData } from "@tanstack/react-query";

import {
  adminApi,
  groupsApi,
  listsApi,
  membersApi,
  publicApi,
  publicGroupsApi,
  wishesApi,
} from "./api";

/**
 * Query keys and fetchers, declared once so a mutation invalidates exactly what a page
 * reads. Kept out of route files: the router's code splitting wants those to export only
 * their Route.
 */
export const listsQuery = { queryKey: ["lists"], queryFn: listsApi.list } as const;

export const listQuery = (listId: string) =>
  ({ queryKey: ["lists", listId], queryFn: () => listsApi.get(listId) }) as const;

export const listPreviewQuery = (listId: string) =>
  ({ queryKey: ["lists", listId, "preview"], queryFn: () => listsApi.preview(listId) }) as const;

export const giftsQuery = (listId: string) =>
  ({ queryKey: ["lists", listId, "gifts"], queryFn: () => listsApi.gifts(listId) }) as const;

export const wishesQuery = (listId: string) =>
  ({ queryKey: ["lists", listId, "wishes"], queryFn: () => wishesApi.list(listId) }) as const;

export const membersQuery = (listId: string) =>
  ({ queryKey: ["lists", listId, "members"], queryFn: () => membersApi.list(listId) }) as const;

export const publicListQuery = (slug: string) =>
  ({ queryKey: ["public", "list", slug], queryFn: () => publicApi.list(slug) }) as const;

export const groupsQuery = { queryKey: ["groups"], queryFn: groupsApi.list } as const;

export const groupQuery = (groupId: string) =>
  ({ queryKey: ["groups", groupId], queryFn: () => groupsApi.get(groupId) }) as const;

/** Personal to the caller (their place, their draw): keyed apart from any list query. */
export const publicGroupQuery = (slug: string) =>
  ({ queryKey: ["public", "group", slug], queryFn: () => publicGroupsApi.get(slug) }) as const;

export const productQuery = (slug: string) =>
  ({ queryKey: ["public", "product", slug], queryFn: () => publicApi.product(slug) }) as const;

export const catalogueQuery = ({
  q,
  occasion,
  category,
  sort = "popular",
}: Omit<CatalogueQuery, "cursor">) =>
  infiniteQueryOptions({
    queryKey: ["public", "catalogue", q ?? "", occasion ?? "", category ?? "", sort],
    queryFn: ({ pageParam }) =>
      publicApi.catalogue({ q, occasion, category, sort, cursor: pageParam || undefined }),
    initialPageParam: "",
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });

export const catalogueCategoriesQuery = (occasion?: ListOccasion) =>
  ({
    queryKey: ["public", "catalogue-categories", occasion ?? ""],
    queryFn: () => publicApi.catalogueCategories({ occasion }),
  }) as const;

export const adminStatsQuery = (days: number) =>
  ({
    queryKey: ["admin", "stats", days],
    queryFn: () => adminApi.stats({ days }),
    // A filter change keeps the previous numbers on screen until the new ones land, instead
    // of swapping the whole page for a spinner.
    placeholderData: keepPreviousData,
  }) as const;

export const adminUsageQuery = (days: number) =>
  ({
    queryKey: ["admin", "usage", days],
    queryFn: () => adminApi.usage({ days }),
    placeholderData: keepPreviousData,
  }) as const;

export const adminSystemQuery = {
  queryKey: ["admin", "system"],
  queryFn: () => adminApi.system(),
  // A live reading: refreshed while the tab is open, like a status board.
  refetchInterval: 30_000,
} as const;

export const adminAffiliateQuery = {
  queryKey: ["admin", "affiliate"],
  queryFn: () => adminApi.affiliate(),
} as const;

export const adminDesignsQuery = (days: number) =>
  ({
    queryKey: ["admin", "designs", days],
    queryFn: () => adminApi.designs({ days }),
    placeholderData: keepPreviousData,
  }) as const;

export const adminSignInsQuery = (days: number) =>
  ({
    queryKey: ["admin", "sign-ins", days],
    queryFn: () => adminApi.signIns({ days }),
    placeholderData: keepPreviousData,
  }) as const;

export const adminProductsQuery = (query: AdminProductsQuery) =>
  ({
    queryKey: [
      "admin",
      "products",
      query.host ?? "",
      query.sort ?? "recent",
      query.filter ?? "all",
    ],
    queryFn: () => adminApi.products(query),
    placeholderData: keepPreviousData,
  }) as const;

export const adminListsQuery = (sort: AdminListSort) =>
  ({
    queryKey: ["admin", "lists", sort],
    queryFn: () => adminApi.lists({ sort }),
    placeholderData: keepPreviousData,
  }) as const;
